import { ScaleLinear, ScaleTime } from "d3";

import Typography from "ds/components/Typography";

import { WorkerUsageData } from "../types";
import { hasWorkerUsageContract, isWorkerUsagePastCycle } from "../utils";
import {
  REFERENCE_BADGE_CHAR_WIDTH,
  REFERENCE_BADGE_HEIGHT,
  REFERENCE_BADGE_PADDING_X,
} from "./constants";
import styles from "./styles.module.css";

type ReferenceLabelDatum = {
  key: string;
  text: string;
  borderColor: string;
  backgroundColor: string;
  x: number;
  y: number;
  width: number;
};

type GetReferenceLineLabelsParams = {
  usage: WorkerUsageData;
  lastPercentilePoint?: { day: Date; value: number };
  percentile: number;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  xMax: number;
};

// Badges are pinned to their line's endpoint and drawn rightward from it, unless
// that would overhang the plot — then they flip and draw leftward instead.
const placeBadge = (text: string, anchorX: number, xMax: number) => {
  const width = text.length * REFERENCE_BADGE_CHAR_WIDTH + REFERENCE_BADGE_PADDING_X * 2;

  return { width, x: anchorX + width > xMax ? anchorX - width : anchorX };
};

export const getReferenceLineLabels = ({
  usage,
  lastPercentilePoint,
  percentile,
  xScale,
  yScale,
  xMax,
}: GetReferenceLineLabelsParams): ReferenceLabelDatum[] => {
  const labels: ReferenceLabelDatum[] = [];
  const showsContractLimit = hasWorkerUsageContract(usage) && !usage.multiAccountContract;

  const contractLabel: ReferenceLabelDatum | undefined = showsContractLimit
    ? {
        key: "contract",
        text: "Contract",
        borderColor: "var(--semantic-color-border-decoration-fuchsia)",
        backgroundColor: "var(--semantic-color-background-decoration-fuchsia-subdued)",
        ...placeBadge("Contract", xScale(usage.cycleEnd), xMax),
        y: yScale(usage.contractedWorkers),
      }
    : undefined;

  if (contractLabel) labels.push(contractLabel);

  if (!lastPercentilePoint) return labels;

  const text = isWorkerUsagePastCycle(usage) ? `P${percentile}` : `P${percentile} to date`;
  const placement = placeBadge(text, xScale(lastPercentilePoint.day), xMax);
  let badgeY = yScale(lastPercentilePoint.value);

  // Where the two badges overlap, stack them edge to edge while keeping them
  // ordered like the values they label.
  if (
    contractLabel &&
    placement.x < contractLabel.x + contractLabel.width &&
    contractLabel.x < placement.x + placement.width &&
    Math.abs(badgeY - contractLabel.y) < REFERENCE_BADGE_HEIGHT
  ) {
    badgeY =
      badgeY > contractLabel.y
        ? contractLabel.y + REFERENCE_BADGE_HEIGHT
        : contractLabel.y - REFERENCE_BADGE_HEIGHT;
  }

  labels.push({
    key: "percentile",
    text,
    borderColor: "var(--semantic-color-border-decoration-orange)",
    backgroundColor: "var(--semantic-color-background-decoration-orange-subdued)",
    ...placement,
    y: badgeY,
  });

  return labels;
};

type ConcurrencyChartReferenceLineLabelsProps = {
  labels: ReferenceLabelDatum[];
};

const ConcurrencyChartReferenceLineLabels = ({
  labels,
}: ConcurrencyChartReferenceLineLabelsProps) => (
  <>
    {labels.map((label) => (
      <foreignObject
        key={label.key}
        x={label.x}
        y={label.y - REFERENCE_BADGE_HEIGHT / 2}
        width={label.width}
        height={REFERENCE_BADGE_HEIGHT}
        overflow="visible"
        aria-hidden
      >
        <span
          className={styles.referenceBadge}
          style={{ backgroundColor: label.backgroundColor, borderColor: label.borderColor }}
        >
          <Typography tag="span" variant="p-body4" color="primary">
            {label.text}
          </Typography>
        </span>
      </foreignObject>
    ))}
  </>
);

ConcurrencyChartReferenceLineLabels.displayName =
  "UsageWorkers.ConcurrencyChart.ReferenceLineLabels";

export default ConcurrencyChartReferenceLineLabels;
